import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Scale, AlertTriangle, Shield, FileCheck, Users } from "lucide-react";

const Indemnity = () => {
  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-slate-600 hover:text-slate-800">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Indemnity & Liability
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Indemnity & Liability</h1>
              <p className="text-slate-500">Understanding responsibilities when using KiddiPlay Club</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <div className="p-6 bg-amber-50 border border-amber-200 rounded-xl mb-8">
              <p className="text-amber-800 font-medium">
                <strong>Important:</strong> By using KiddiPlay Club, you acknowledge that all playdates and meetups are arranged at your own discretion and risk. Please read this document carefully.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Users className="w-5 h-5 text-amber-500" />
                Your Responsibilities as a Parent/Guardian
              </h2>
              <p className="text-slate-600 mb-4">When using KiddiPlay Club, you agree that:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>You are solely responsible for the supervision and safety of your children at all times</li>
                <li>You will not leave your children unsupervised with individuals you have met through our platform</li>
                <li>You are responsible for verifying the identity and suitability of anyone you choose to meet</li>
                <li>You will assess the safety and appropriateness of any meeting location</li>
                <li>You understand that KiddiPlay Club does not conduct background checks on users</li>
                <li>You will exercise appropriate caution and judgement in all interactions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Shield className="w-5 h-5 text-amber-500" />
                What KiddiPlay Club Is NOT Responsible For
              </h2>
              <p className="text-slate-600 mb-4">KiddiPlay Club expressly disclaims responsibility for:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Any injury, harm, or damage occurring during or as a result of meetups arranged through our platform</li>
                <li>The actions, behaviour, or conduct of any users, whether online or in person</li>
                <li>The accuracy of information provided by users on their profiles</li>
                <li>Any disputes arising between users of our platform</li>
                <li>Loss, theft, or damage to personal property during meetups</li>
                <li>The quality or safety of any venues or activities found through our marketplace</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Share a Sitter / Babysitting Arrangements
              </h2>
              <p className="text-slate-600 mb-4">
                If you use our "Buddy UP!" feature to share childcare or activities with other families:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>You are responsible for ensuring any carer is suitable and trustworthy</li>
                <li>We recommend only using carers with valid DBS checks (you are responsible for verification)</li>
                <li>All payment arrangements are between the families involved - we are not a party to these agreements</li>
                <li>You remain legally responsible for your children at all times</li>
                <li>We strongly recommend having a written agreement covering emergency contacts, allergies, and permissions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <FileCheck className="w-5 h-5 text-amber-500" />
                Insurance Recommendations
              </h2>
              <p className="text-slate-600 mb-4">We strongly recommend that all users:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Maintain adequate home insurance that covers visitors (if hosting playdates at home)</li>
                <li>Have appropriate public liability cover if regularly hosting activities</li>
                <li>Ensure any carers used have their own public liability insurance</li>
                <li>Check your motor insurance covers other children as passengers</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Indemnification</h2>
              <p className="text-slate-600">
                You agree to indemnify, defend, and hold harmless KiddiPlay Club, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Your use of the platform</li>
                <li>Any meetups or interactions arranged through the platform</li>
                <li>Your breach of these terms or any applicable law</li>
                <li>Any content you post or share on the platform</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Limitation of Liability</h2>
              <p className="text-slate-600">
                To the maximum extent permitted by UK law, KiddiPlay Club's total liability for any claims arising from your use of our platform is limited to the amount you have paid us in subscription fees in the 12 months prior to the claim (or £100 if you are a free user).
              </p>
              <p className="text-slate-600 mt-4">
                Nothing in these terms limits our liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded by law.
              </p>
            </section>

            <section className="p-6 bg-amber-50 rounded-xl">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Acceptance</h2>
              <p className="text-slate-600">
                By creating an account and using KiddiPlay Club, you confirm that you have read, understood, and agree to be bound by this Indemnity & Liability agreement.
              </p>
              <p className="text-slate-600 mt-4">
                <strong>Questions?</strong> Contact us at <a href="mailto:legal@kiddiplayclub.com" className="text-amber-600 underline">legal@kiddiplayclub.com</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Indemnity;
